import { useState, useEffect } from 'react';
import '@/App.css';
// Attach global axios interceptors (auto-retry transient errors, default
// timeout, friendly toast). MUST be imported before any axios call site
// resolves — i.e., at the very top of App.js.
import '@/utils/axiosBootstrap';
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { startTokenRefreshInterval, stopTokenRefreshInterval } from './utils/axiosConfig';
import AppShell from '@/components/AppShell/AppShell';
import LandingPage from '@/pages/LandingPage';
import LoginPage from '@/pages/LoginPage';
import TherapistDashboard from '@/pages/TherapistDashboard';
import ParentDashboard from '@/pages/ParentDashboard';
import ChildProfile from '@/pages/ChildProfile';
import ActivityLibrary from '@/pages/ActivityLibrary';
import ChunkBank from '@/pages/ChunkBank';
import WeeklyFocusPage from '@/pages/WeeklyFocusPage';
import ParentWeeklyFocus from '@/pages/ParentWeeklyFocus';
import ProgressTracking from '@/pages/ProgressTracking';
import JoinWithCode from '@/pages/JoinWithCode';
import GLPEducation from '@/pages/GLPEducation';
import AdminDashboard from '@/pages/AdminDashboard';
import AdminAnalytics from '@/pages/AdminAnalytics';
import LiteAdmin from '@/pages/LiteAdmin';
import Invoicing from '@/pages/Invoicing';
import ResourceLibrary from '@/pages/ResourceLibrary';
import ContinuedEducation from '@/pages/ContinuedEducation';
import ParentAppWelcome from '@/pages/parentapp/ParentAppWelcome';
import ParentAppSignup from '@/pages/parentapp/ParentAppSignup';
import ParentAppLogin from '@/pages/parentapp/ParentAppLogin';
import ParentAppOnboarding from '@/pages/parentapp/ParentAppOnboarding';
import ParentAppHome from '@/pages/parentapp/ParentAppHome';
import ParentAppGoals from '@/pages/parentapp/ParentAppGoals';
import ParentAppLanguageMap from '@/pages/parentapp/ParentAppLanguageMap';
import ParentAppSettings from '@/pages/parentapp/ParentAppSettings';
import ParentAppCollaborators from '@/pages/parentapp/ParentAppCollaborators';
import ParentAppWhereTheyAre from '@/pages/parentapp/ParentAppWhereTheyAre';
import ParentAppPricing from '@/pages/parentapp/ParentAppPricing';
import ParentContactSupport from '@/pages/parentapp/ParentContactSupport';
import ParentAppMyToys from '@/pages/parentapp/ParentAppMyToys';
import ClinicalParentLinks from '@/pages/clinical/ClinicalParentLinks';
import ParentHome from '@/pages/ParentHome';
import WithTherapistView from '@/pages/parentapp/WithTherapistView';
import DualRoleTabBar from '@/components/DualRoleTabBar';
import ErrorBoundary from '@/components/ErrorBoundary';
import ParentAppAuthCallback from '@/pages/parentapp/ParentAppAuthCallback';
import ParentAcceptInvite from '@/pages/parentapp/ParentAcceptInvite';
import AdminParentMembers from '@/pages/admin/AdminParentMembers';
import AdminBillingLookup from '@/pages/admin/AdminBillingLookup';
import AdminPartnerCodes from '@/pages/admin/AdminPartnerCodes';
import AdminTodos from '@/pages/admin/AdminTodos';
import AdminPractices from '@/pages/admin/AdminPractices';
import AdminParentDataLookup from '@/pages/admin/AdminParentDataLookup';
import AdminPFPAnalytics from '@/pages/admin/AdminPFPAnalytics';
import AdminPFPPendingInvites from '@/pages/admin/AdminPFPPendingInvites';
import TherapistTimetable from '@/pages/TherapistTimetable';
import TherapistDiary from '@/pages/TherapistDiary';
import AdminPracticeParents from '@/pages/admin/AdminPracticeParents';
import PracticePfpBillingReturn from '@/pages/PracticePfpBillingReturn';
import PfpAcceptInvite from '@/pages/PfpAcceptInvite';
import SchoolAdminHome from '@/pages/SchoolAdminHome';
import PfpTherapistHome from '@/pages/PfpTherapistHome';
import TherapistLiteWeekly from '@/pages/TherapistLiteWeekly';
import TherapistSettings from '@/pages/TherapistSettings';
import PracticeAdmin from '@/pages/PracticeAdmin';
import BillingSuccess from '@/pages/BillingSuccess';
import HIPAACompliance from '@/pages/HIPAACompliance';
import RefundPolicy from '@/pages/RefundPolicy';
import AssessmentModule from '@/pages/AssessmentModule';
import ComprehensiveReportGenerator from '@/pages/ComprehensiveReportGenerator';
import HybridReportGenerator from '@/pages/HybridReportGenerator';
import ReportGeneration from '@/pages/ReportGeneration';
import SessionNotes from '@/pages/SessionNotes';
import SharedNotesSummary from '@/pages/SharedNotesSummary';
import TermsOfService from '@/pages/TermsOfService';
import ParentNotifications from '@/pages/ParentNotifications';
import ParentFeedback from '@/pages/ParentFeedback';
import ParentProgressPage from '@/pages/ParentProgressPage';
import AdminDaysheet from '@/pages/AdminDaysheet';
import ResourceArticle from '@/pages/ResourceArticle';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import LiteEntry from '@/pages/lite/LiteEntry';
import LiteHub from '@/pages/lite/LiteHub';
import ReportBuilder from '@/pages/lite/tools/ReportBuilder';
import ParentSummary from '@/pages/lite/tools/ParentSummary';
import GLPGuide from '@/pages/lite/tools/GLPGuide';
import ActivityGenerator from '@/pages/lite/tools/ActivityGenerator';
import SessionToHomeBridge from '@/pages/lite/tools/SessionToHomeBridge';
import LiteBillingSuccess from '@/pages/lite/LiteBillingSuccess';
import LiteForgetMe from '@/pages/lite/LiteForgetMe';
import Subprocessors from '@/pages/Subprocessors';
import SponsorDashboard from '@/pages/SponsorDashboard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ProtectedRoute({ children, allowedRoles }) {
  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || 'null');

  if (!token || !user) {
    return <Navigate to="/login" replace />;
  }

  // PlayFirst Parent users live in a separate product surface — never let
  // them fall into the therapist/parent clinical dashboards.
  if (user.role === 'parent_self') {
    return <Navigate to="/parent-app/home" replace />;
  }

  // School admins (Phase 9.3, PFP-only) always land on /school-admin,
  // which is a standalone layout — never wrap them in AppShell.
  if (user.role === 'school_admin') {
    return <Navigate to="/school-admin" replace />;
  }

  // PFP-only therapists get a stripped-down home; no AppShell.
  if (user.role === 'therapist' && user.plan_type === 'pfp_only') {
    return <Navigate to="/pfp-therapist-home" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/login" replace />;
  }

  return <AppShell user={user}>{children}</AppShell>;
}


/**
 * Feb 2026 — Legacy `/child/:id` URLs (used to be sprinkled through
 * older navigate/Link calls) redirect to the canonical `/children/:id`.
 * Preserves the ?tab=…  query string.
 */
function LegacyChildRedirect() {
  const { id } = useParams();
  const location = useLocation();
  return <Navigate to={`/children/${id}${location.search || ''}`} replace />;
}


/**
 * Catch-all for any unmatched route so the app never renders as a blank
 * white page. Sends to the role-appropriate home if signed-in, else to
 * the landing/login page.
 */
function CatchAllRedirect() {
  const raw = localStorage.getItem('user');
  let target = '/login';
  if (raw) {
    try {
      const u = JSON.parse(raw);
      const r = u?.role;
      if (r === 'parent_self') target = '/parent-app/home';
      else if (r === 'school_admin') target = '/school-admin';
      else if (r === 'therapist' || r === 'admin' || r === 'parent') target = '/';
      else target = '/';
    } catch {
      target = '/';
    }
  }
  return <Navigate to={target} replace />;
}


function App() {  const [user, setUser] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    if (token && storedUser) {
      setUser(JSON.parse(storedUser));
      // Start token refresh interval when user is logged in
      startTokenRefreshInterval();
    }
    
    // Cleanup on unmount
    return () => stopTokenRefreshInterval();
  }, []);

  const handleLogout = () => {
    stopTokenRefreshInterval();
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    window.location.href = '/login';
  };

  return (
    <div className="App">
      <BrowserRouter>
        {/* Dual-role parents: persistent tab bar visible on every parent
            surface (child pages, PFP sub-pages, notifications, etc). Prior
            behaviour only showed tabs on /parent-home which meant a tap
            on anything permanently dismissed them. */}
        <DualRoleTabBar user={user} />
        <ErrorBoundary>
        <Routes>
          <Route path="/login" element={<LandingPage onLogin={setUser} />} />
          
          {/* Public Resource Articles - No auth required */}
          <Route path="/resources/:slug" element={<ResourceArticle />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />

          {/* PlayFirst Parent (self-managed parent product, separate from Clinical) */}
          <Route path="/parent-app" element={<ParentAppWelcome />} />
          <Route path="/parent-app/signup" element={<ParentAppSignup />} />
          <Route path="/parent-app/login" element={<ParentAppLogin />} />
          <Route path="/parent-app/onboarding" element={<ParentAppOnboarding />} />
          <Route path="/parent-app/home" element={<ParentAppHome />} />
          {/* Feb 2026 — "With my therapist" tab (dual-role parents only).
              Lives inside the PFP route tree so PFP entitlement + styling
              are inherited. See DualRoleTabBar for the tab click wiring. */}
          <Route path="/parent-app/with-therapist" element={<WithTherapistView />} />
          <Route path="/parent-app/language-map" element={<ParentAppLanguageMap />} />
          <Route path="/parent-app/goals" element={<ParentAppGoals />} />
          <Route path="/parent-app/settings" element={<ParentAppSettings />} />
          <Route path="/sponsor" element={<SponsorDashboard />} />
          <Route path="/parent-app/collaborators" element={<ParentAppCollaborators />} />
          <Route path="/parent-app/where-they-are" element={<ParentAppWhereTheyAre />} />
          <Route path="/parent-app/pricing" element={<ParentAppPricing />} />
          <Route path="/parent-app/contact" element={<ParentContactSupport />} />
          <Route path="/parent-app/toys" element={<ParentAppMyToys />} />
          <Route path="/clinical/parent-links" element={<ClinicalParentLinks />} />
          <Route path="/parent-home" element={<Navigate to="/" replace />} />
          <Route path="/parent-app/auth-callback" element={<ParentAppAuthCallback />} />
          <Route path="/parent-app/accept-invite" element={<ParentAcceptInvite />} />
          
          <Route
            path="/"
            element={
              <ProtectedRoute>
                {user?.role === 'school_admin' ? (
                  <Navigate to="/school-admin" replace />
                ) : user?.role === 'therapist' && user?.plan_type === 'pfp_only' ? (
                  <Navigate to="/pfp-therapist-home" replace />
                ) : user?.role === 'therapist' ? (
                  <TherapistDashboard onLogout={handleLogout} />
                ) : user?.capabilities?.pfp && user?.capabilities?.clinical_parent ? (
                  // Dual-app parent: land on their Clinical dashboard.
                  // The global DualRoleTabBar handles switching between
                  // Clinical (this page) and PFP (/parent-app/home) so
                  // there's no need to render the /parent-home wrapper.
                  // Previous behaviour: /parent-home wrapper with its own
                  // tabs — but tabs vanished on any inner navigation.
                  <ParentDashboard onLogout={handleLogout} />
                ) : user?.capabilities?.pfp && !user?.capabilities?.clinical_parent ? (
                  // PFP-only parent (typical self-signup) — send to the
                  // existing PFP home. Using window.location because the
                  // ParentAppHome sits at a nested route.
                  <Navigate to="/parent-app/home" replace />
                ) : (
                  <ParentDashboard onLogout={handleLogout} />
                )}
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/join-with-code"
            element={
              <ProtectedRoute allowedRoles={['parent']}>
                <JoinWithCode onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/children/:id"
            element={
              <ProtectedRoute>
                <ChildProfile onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/activities"
            element={
              <ProtectedRoute allowedRoles={['therapist']}>
                <ActivityLibrary onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/chunks/:childId"
            element={
              <ProtectedRoute>
                <ChunkBank onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/weekly-focus/:childId"
            element={
              <ProtectedRoute>
                {user?.role === 'therapist' ? (
                  <WeeklyFocusPage onLogout={handleLogout} />
                ) : (
                  <ParentWeeklyFocus onLogout={handleLogout} />
                )}
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/progress/:childId"
            element={
              <ProtectedRoute>
                <ProgressTracking onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/parent/notifications"
            element={
              <ProtectedRoute allowedRoles={['parent']}>
                <ParentNotifications onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/parent/feedback/:childId?"
            element={
              <ProtectedRoute allowedRoles={['parent']}>
                <ParentFeedback onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/parent-progress/:childId"
            element={
              <ProtectedRoute allowedRoles={['therapist']}>
                <ParentProgressPage onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/glp-education"
            element={
              <ProtectedRoute>
                <GLPEducation onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/learn"
            element={
              <ProtectedRoute>
                <ResourceLibrary onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />

          {/* /resources — same component, kept in sync with the sidebar nav
              (navConfig.js sends users here). Without this route the page
              was rendering blank when clicked from the menu. */}
          <Route
            path="/resources"
            element={
              <ProtectedRoute>
                <ResourceLibrary onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />

          <Route
            path="/continued-education"
            element={
              <ProtectedRoute allowedRoles={['therapist']}>
                <ContinuedEducation onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin"
            element={
              <ProtectedRoute allowedRoles={['therapist']}>
                <AdminDashboard onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin/analytics"
            element={
              <ProtectedRoute allowedRoles={['therapist']}>
                <AdminAnalytics onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/lite"
            element={
              <ProtectedRoute allowedRoles={['therapist']}>
                <LiteAdmin onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/parent-members"
            element={
              <ProtectedRoute allowedRoles={['therapist']}>
                <AdminParentMembers />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/billing-lookup"
            element={
              <ProtectedRoute allowedRoles={['therapist']}>
                <AdminBillingLookup />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/partner-codes"
            element={
              <ProtectedRoute allowedRoles={['therapist']}>
                <AdminPartnerCodes />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/todos"
            element={
              <ProtectedRoute allowedRoles={['therapist']}>
                <AdminTodos />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/practices"
            element={
              <ProtectedRoute allowedRoles={['therapist']}>
                <AdminPractices />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/practices/:practiceId/parents"
            element={
              <ProtectedRoute allowedRoles={['therapist']}>
                <AdminPracticeParents />
              </ProtectedRoute>
            }
          />

          {/* Super-admin diagnostic — "why doesn't this parent see her data?" */}
          <Route
            path="/admin/parent-lookup"
            element={
              <ProtectedRoute allowedRoles={['therapist']}>
                <AdminParentDataLookup />
              </ProtectedRoute>
            }
          />

          {/* Super-admin — PFP user analytics */}
          <Route
            path="/admin/pfp-analytics"
            element={
              <ProtectedRoute allowedRoles={['therapist']}>
                <AdminPFPAnalytics />
              </ProtectedRoute>
            }
          />

          {/* Super-admin — PFP pending-parent invite manager (Feb 2026) */}
          <Route
            path="/admin/pfp-pending-invites"
            element={
              <ProtectedRoute allowedRoles={['therapist']}>
                <AdminPFPPendingInvites />
              </ProtectedRoute>
            }
          />

          {/* Therapist — recurring weekly timetable */}
          <Route
            path="/timetable"
            element={
              <ProtectedRoute allowedRoles={['therapist']}>
                <TherapistTimetable />
              </ProtectedRoute>
            }
          />

          {/* Therapist — diary & reminders */}
          <Route
            path="/diary"
            element={
              <ProtectedRoute allowedRoles={['therapist']}>
                <TherapistDiary />
              </ProtectedRoute>
            }
          />

          <Route path="/practice/pfp-billing/return" element={<PracticePfpBillingReturn />} />

          {/* Phase 9.3 — PFP-only schools (standalone layouts, no AppShell) */}
          <Route path="/pfp-accept-invite" element={<PfpAcceptInvite />} />
          <Route path="/school-admin" element={<SchoolAdminHome />} />
          <Route path="/pfp-therapist-home" element={<PfpTherapistHome />} />
          {/* Feb 2026 — Therapist-Lite public weekly goal-setter (token in URL) */}
          <Route path="/therapist-lite/weekly/:token" element={<TherapistLiteWeekly />} />

          <Route
            path="/invoicing"
            element={
              <ProtectedRoute allowedRoles={['therapist']}>
                <Invoicing onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/invoicing/:childId"
            element={
              <ProtectedRoute allowedRoles={['therapist']}>
                <Invoicing onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin/daysheet"
            element={
              <ProtectedRoute allowedRoles={['therapist']}>
                <AdminDaysheet onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/settings"
            element={
              <ProtectedRoute allowedRoles={['therapist']}>
                <TherapistSettings onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/shared-notes"
            element={
              <ProtectedRoute allowedRoles={['therapist']}>
                <SharedNotesSummary onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/practice"
            element={
              <ProtectedRoute allowedRoles={['therapist']}>
                <PracticeAdmin onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/billing/success"
            element={
              <ProtectedRoute allowedRoles={['therapist']}>
                <BillingSuccess />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/assessments/:childId"
            element={
              <ProtectedRoute allowedRoles={['therapist']}>
                <AssessmentModule onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/assessments/:childId/:assessmentId/comprehensive"
            element={
              <ProtectedRoute allowedRoles={['therapist']}>
                <ComprehensiveReportGenerator onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/assessments/:childId/:assessmentId/report"
            element={
              <ProtectedRoute allowedRoles={['therapist']}>
                <HybridReportGenerator onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/session-notes/:childId"
            element={
              <ProtectedRoute allowedRoles={['therapist']}>
                <SessionNotes onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/progress-reports/:childId"
            element={
              <ProtectedRoute allowedRoles={['therapist']}>
                <ReportGeneration onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/hipaa"
            element={<HIPAACompliance />}
          />
          
          <Route
            path="/terms"
            element={<TermsOfService />}
          />
          
          <Route
            path="/refund-policy"
            element={<RefundPolicy />}
          />

          {/* PlayFirst Lite (US) — stateless tools */}
          <Route path="/us" element={<LiteEntry />} />
          <Route path="/us/tools" element={<LiteHub />} />
          <Route path="/us/tools/report" element={<ReportBuilder />} />
          <Route path="/us/tools/parent" element={<ParentSummary />} />
          <Route path="/us/tools/glp" element={<GLPGuide />} />
          <Route path="/us/tools/activity" element={<ActivityGenerator />} />
          <Route path="/us/tools/session-bridge" element={<SessionToHomeBridge />} />
          <Route path="/lite/tools/session-bridge" element={<SessionToHomeBridge />} />
          <Route path="/us/billing/success" element={<LiteBillingSuccess />} />
          <Route path="/us/forget-me" element={<LiteForgetMe />} />
          <Route path="/lite/forget-me" element={<LiteForgetMe />} />
          <Route path="/lite/subprocessors" element={<Subprocessors />} />
          <Route path="/subprocessors" element={<Subprocessors />} />
          {/* Feb 2026 — legacy alias + catch-all so a wrong URL never
              renders as a blank white page (the "back button blanks" bug
              on production was /child/:id when the canonical path is
              /children/:id). */}
          <Route path="/child/:id" element={<LegacyChildRedirect />} />
          <Route path="*" element={<CatchAllRedirect />} />
        </Routes>
        </ErrorBoundary>
      </BrowserRouter>
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;
